import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_THRESHOLD_PX = 24;

export function useScrollToBottom(thresholdPx = DEFAULT_THRESHOLD_PX) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const checkIsAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    setIsAtBottom(distanceFromBottom <= thresholdPx);
  }, [thresholdPx]);

  const scrollToBottom = useCallback(
    (options?: { behavior?: ScrollBehavior }) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      container.scrollTo({
        top: container.scrollHeight,
        behavior: options?.behavior ?? 'smooth',
      });
    },
    [],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let previousScrollHeight = container.scrollHeight;

    const pinIfWasAtBottom = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      const heightDelta = scrollHeight - previousScrollHeight;
      previousScrollHeight = scrollHeight;

      if (heightDelta <= 0) {
        return;
      }

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const wasAtBottom = distanceFromBottom - heightDelta <= thresholdPx;
      if (!wasAtBottom) {
        return;
      }

      container.scrollTop = scrollHeight - clientHeight;
      setIsAtBottom(true);
    };

    const resizeObserver = new ResizeObserver(() => {
      pinIfWasAtBottom();
    });

    for (const child of container.children) {
      resizeObserver.observe(child);
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            resizeObserver.observe(node);
          }
        }
      }
      pinIfWasAtBottom();
    });

    mutationObserver.observe(container, { childList: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [thresholdPx]);

  return {
    containerRef,
    isAtBottom,
    checkIsAtBottom,
    scrollToBottom,
  };
}
